import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';

import AfasArticlePiP from './AfasArticlePiP';
import JobHistoryPiP from './JobHistoryPiP';
import JobLogsModal from './JobLogsModal';
import AdminAIAssistantDrawer from './AdminAIAssistantDrawer';
import { isDevEnvironment } from '../utils/environment';
import { CAP, can } from '../utils/permissions';
import { apiFetch } from '../utils/apiAuth';

const GlobalAdminFAB = ({ authUser }) => {
  const [isFabOpen, setIsFabOpen] = useState(false);
  const [showAiAssistant, setShowAiAssistant] = useState(false);
  const [showAfasPiP, setShowAfasPiP] = useState(false);
  const [afasArticles, setAfasArticles] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(false);

  const [currentJobName, setCurrentJobName] = useState(null);
  const [showJobHistoryPiP, setShowJobHistoryPiP] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [showJobLogsPiP, setShowJobLogsPiP] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || '';

  // Listen for custom events to open PiPs from other parts of the app
  useEffect(() => {
    const handleOpenAfasPip = () => {
      setShowAfasPiP(true);
    };
    const handleOpenJobLogs = (e) => {
      if (e.detail && e.detail.jobId) {
        setCurrentJobId(e.detail.jobId);
        setShowJobLogsPiP(true);
      }
    };

    const handleOpenAiAssistant = () => {
      setShowAiAssistant(true);
    };

    window.addEventListener('open-afas-pip', handleOpenAfasPip);
    window.addEventListener('open-job-logs', handleOpenJobLogs);
    window.addEventListener('open-ai-assistant', handleOpenAiAssistant);

    return () => {
      window.removeEventListener('open-afas-pip', handleOpenAfasPip);
      window.removeEventListener('open-job-logs', handleOpenJobLogs);
      window.removeEventListener('open-ai-assistant', handleOpenAiAssistant);
    };
  }, []);

  useEffect(() => {
    if (showAfasPiP && afasArticles.length === 0 && !loadingArticles) {
      const fetchArticles = async () => {
        setLoadingArticles(true);
        try {
          const res = await apiFetch(`${apiUrl}/api/afas-articles`);
          if (res.ok) {
            const data = await res.json();
            setAfasArticles(data);
          } else {
            toast.error("Failed to fetch AFAS articles.");
          }
        } catch (error) {
          console.error(error);
          toast.error("Error fetching AFAS articles.");
        } finally {
          setLoadingArticles(false);
        }
      };
      fetchArticles();
    }
  }, [showAfasPiP, afasArticles.length, loadingArticles, apiUrl]);

  const location = useLocation();
  const isAdminPath = location.pathname.startsWith('/admin') || 
                      location.pathname.startsWith('/workspaces') || 
                      location.pathname.startsWith('/email-templates');

  // The helper tools it opens are all reads (AFAS articles, job history and
  // logs), so viewAdminScreens is the right gate: Read-only may look, and the
  // endpoints behind them enforce the same capability server-side.
  if (!can(authUser, CAP.VIEW_ADMIN_SCREENS) || !isAdminPath) {
    return null;
  }

  const openJobHistory = (baseName) => {
    setCurrentJobName(isDevEnvironment() ? `${baseName}_test` : baseName);
    setShowJobHistoryPiP(true);
    setIsFabOpen(false);
  };

  return (
    <>
      {showAfasPiP && (
        <AfasArticlePiP
          articles={afasArticles}
          onClose={() => setShowAfasPiP(false)}
        />
      )}

      {currentJobId && showJobLogsPiP && (
        <JobLogsModal
          jobId={currentJobId}
          onClose={() => setShowJobLogsPiP(false)}
        />
      )}

      {showJobHistoryPiP && currentJobName && (
        <JobHistoryPiP
          jobName={currentJobName}
          onSelectJob={(jobId) => {
            setCurrentJobId(jobId);
            setShowJobLogsPiP(true);
            setShowJobHistoryPiP(false);
          }}
          onClose={() => setShowJobHistoryPiP(false)}
        />
      )}

      {showAiAssistant && (
        <AdminAIAssistantDrawer
          isOpen={showAiAssistant}
          onClose={() => setShowAiAssistant(false)}
          currentContext={{
            path: location.pathname,
            currentDomain: new URLSearchParams(location.search).get('domain'),
            orderId: new URLSearchParams(location.search).get('order') || new URLSearchParams(location.search).get('voucher'),
          }}
        />
      )}

      {/* Floating Action Buttons */}
      <div className="fixed bottom-6 left-6 z-[90] flex flex-col-reverse items-start gap-3">
        {/* Helper Tools FAB */}
        <div className="flex flex-col-reverse items-start gap-2">
          <button
            onClick={() => setIsFabOpen(!isFabOpen)}
            className="w-14 h-14 bg-primary text-white rounded-full shadow-lg flex items-center justify-center hover:bg-opacity-90 transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            title="Helper Tools"
          >
            {isFabOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
            )}
          </button>

        {isFabOpen && (
          <div className="flex flex-col gap-3 mb-2 animate-in slide-in-from-bottom-2 fade-in duration-200 origin-bottom-left items-start">
            <button
              onClick={() => {
                setShowAfasPiP(!showAfasPiP);
                setIsFabOpen(false);
              }}
              className="flex items-center gap-3 group focus:outline-none"
            >
              <div className={`flex items-center justify-center w-12 h-12 rounded-full shadow-lg transition-all duration-200 group-hover:scale-105 ${showAfasPiP ? 'bg-primary text-white' : 'bg-white text-gray-500 group-hover:bg-fuchsia-50 group-hover:text-primary'}`}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <span className={`px-3 py-1.5 rounded-lg shadow-md text-sm font-semibold transition-colors duration-200 ${showAfasPiP ? 'bg-primary text-white' : 'bg-white text-gray-700 group-hover:text-primary'}`}>
                AFAS Articles Code
              </span>
            </button>
            <button
              onClick={() => openJobHistory('sync-customers-job')}
              className="flex items-center gap-3 group focus:outline-none"
            >
              <div className="flex items-center justify-center w-12 h-12 rounded-full shadow-lg transition-all duration-200 group-hover:scale-105 bg-white text-gray-500 group-hover:bg-fuchsia-50 group-hover:text-primary">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="px-3 py-1.5 rounded-lg bg-white shadow-md text-sm font-semibold transition-colors duration-200 text-gray-700 group-hover:text-primary">
                Sync Customers Logs
              </span>
            </button>
            <button
              onClick={() => openJobHistory('check-renewals-job')}
              className="flex items-center gap-3 group focus:outline-none"
            >
              <div className="flex items-center justify-center w-12 h-12 rounded-full shadow-lg transition-all duration-200 group-hover:scale-105 bg-white text-gray-500 group-hover:bg-fuchsia-50 group-hover:text-primary">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="px-3 py-1.5 rounded-lg bg-white shadow-md text-sm font-semibold transition-colors duration-200 text-gray-700 group-hover:text-primary">
                Check Renewals Logs
              </span>
            </button>
            <button
              onClick={() => openJobHistory('create-renewal-invoices-job')}
              className="flex items-center gap-3 group focus:outline-none"
            >
              <div className="flex items-center justify-center w-12 h-12 rounded-full shadow-lg transition-all duration-200 group-hover:scale-105 bg-white text-gray-500 group-hover:bg-fuchsia-50 group-hover:text-primary">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="px-3 py-1.5 rounded-lg bg-white shadow-md text-sm font-semibold transition-colors duration-200 text-gray-700 group-hover:text-primary">
                Create Invoices Logs
              </span>
            </button>
            <button
              onClick={() => openJobHistory('send-renewal-reminders-job')}
              className="flex items-center gap-3 group focus:outline-none"
            >
              <div className="flex items-center justify-center w-12 h-12 rounded-full shadow-lg transition-all duration-200 group-hover:scale-105 bg-white text-gray-500 group-hover:bg-fuchsia-50 group-hover:text-primary">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="px-3 py-1.5 rounded-lg bg-white shadow-md text-sm font-semibold transition-colors duration-200 text-gray-700 group-hover:text-primary">
                Send Reminders Logs
              </span>
            </button>
            <button
              onClick={() => openJobHistory('afas-flexible-invoices-job')}
              className="flex items-center gap-3 group focus:outline-none"
            >
              <div className="flex items-center justify-center w-12 h-12 rounded-full shadow-lg transition-all duration-200 group-hover:scale-105 bg-white text-gray-500 group-hover:bg-fuchsia-50 group-hover:text-primary">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="px-3 py-1.5 rounded-lg bg-white shadow-md text-sm font-semibold transition-colors duration-200 text-gray-700 group-hover:text-primary">
                AFAS Flexible Invoices Logs
              </span>
            </button>
            <button
              onClick={() => openJobHistory('afas-gcp-invoices-job')}
              className="flex items-center gap-3 group focus:outline-none"
            >
              <div className="flex items-center justify-center w-12 h-12 rounded-full shadow-lg transition-all duration-200 group-hover:scale-105 bg-white text-gray-500 group-hover:bg-fuchsia-50 group-hover:text-primary">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="px-3 py-1.5 rounded-lg bg-white shadow-md text-sm font-semibold transition-colors duration-200 text-gray-700 group-hover:text-primary">
                AFAS GCP Invoices Logs
              </span>
            </button>
          </div>
        )}
        </div>

        {/* AI Bot FAB (Admin Only) - Placed directly above Helper Tools */}
        {can(authUser, CAP.ADMIN_WRITE) && (
          <button
            onClick={() => setShowAiAssistant(true)}
            className="w-14 h-14 bg-gradient-to-tr from-[#6B1D52] via-primary to-fuchsia-600 text-white rounded-full shadow-xl flex items-center justify-center hover:scale-105 active:scale-95 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary relative group p-2.5"
            title="Phoenix AI Support Bot (Admin Only)"
          >
            <img
              src="/phoenix-bot.png"
              alt="Phoenix AI Bot"
              className="w-full h-full object-contain filter drop-shadow select-none pointer-events-none"
            />
            <span className="absolute -top-0.5 -right-0.5 flex h-3.5 w-3.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-amber-500 border-2 border-white"></span>
            </span>
          </button>
        )}
      </div>
    </>
  );
};

export default GlobalAdminFAB;
